import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./login.css";
import { API_URL } from "./constants";

const BusinessSetup: React.FC = () => {
  const [formData, setFormData] = useState({
    businessName: "",
    industry: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    websiteUrl: "",
  });

  const [industries, setIndustries] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    // Fetch industries
    fetch(`${API_URL}/api/industries`)
      .then(res => res.json())
      .then(data => setIndustries(data.industries))
      .catch(err => console.error("Failed to load industries:", err));
  }, [navigate]);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.businessName || !formData.industry) {
      setError("Business name and industry are required");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      
      const res = await fetch(`${API_URL}/api/business`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          business_name: formData.businessName,
          industry: formData.industry,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          website_url: formData.websiteUrl,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to create business profile");
        return;
      }

      console.log("Business profile created:", data);

      // Store business data
      localStorage.setItem("business", JSON.stringify(data.business));

      // Redirect to dashboard/home
      navigate("/home");

    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={handleSubmit} style={{ maxWidth: "500px" }}>
        <h2>Business Information</h2>
        <p style={{ color: "#666", marginBottom: "1.5rem" }}>
          Step 2 of 2: Tell us about your business
        </p>

        {error && (
          <div style={{
            background: "#fee",
            color: "#c33",
            padding: "0.75rem",
            borderRadius: "4px",
            marginBottom: "1rem"
          }}>
            {error}
          </div>
        )}

        <div className="input-group">
          <label htmlFor="businessName">Business Name *</label>
          <input
            type="text"
            id="businessName"
            value={formData.businessName}
            onChange={(e) => updateField("businessName", e.target.value)}
            placeholder="Acme Corporation"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="industry">Industry *</label>
          <select
            id="industry"
            value={formData.industry}
            onChange={(e) => updateField("industry", e.target.value)}
            required
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "1rem"
            }}
          >
            <option value="">Select an industry</option>
            {industries.map((ind) => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="websiteUrl">Website URL</label>
          <input
            type="url"
            id="websiteUrl"
            value={formData.websiteUrl}
            onChange={(e) => updateField("websiteUrl", e.target.value)}
            placeholder="https://www.example.com"
          />
        </div>

        <div className="input-group">
          <label htmlFor="address">Address</label>
          <input
            type="text"
            id="address"
            value={formData.address}
            onChange={(e) => updateField("address", e.target.value)}
            placeholder="123 Main St"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "0.5rem" }}>
          <div className="input-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              value={formData.city}
              onChange={(e) => updateField("city", e.target.value)}
              placeholder="New York"
            />
          </div>

          <div className="input-group">
            <label htmlFor="state">State</label>
            <input
              type="text"
              id="state"
              value={formData.state}
              onChange={(e) => updateField("state", e.target.value)}
              placeholder="NY"
              maxLength={2}
              style={{ textTransform: "uppercase" }}
            />
          </div>

          <div className="input-group">
            <label htmlFor="zipCode">ZIP</label>
            <input
              type="text"
              id="zipCode"
              value={formData.zipCode}
              onChange={(e) => updateField("zipCode", e.target.value)}
              placeholder="10001"
            />
          </div>
        </div>

        <button 
          type="submit" 
          className="login-btn"
          disabled={loading}
          style={{ marginTop: "1.5rem" }}
        >
          {loading ? "Saving..." : "Complete Setup"}
        </button>
      </form>
    </div>
  );
};

export default BusinessSetup;